import React from 'react';
import PropTypes from 'prop-types';

import { styled } from '@mui/material';

import {
  Button, Divider, Menu, MenuItem,
  Typography,
} from '@datanomik/commons-ui';

import { STATUS_ACTIVE } from '@/assets/AppConstants';

import { useWorkspaceContext } from '@/contexts/workspace';

import { useNavigateRedirect } from '@/hooks/useNavigate';

import { ArrowDownSLine } from '@datanomik/commons-ui/icons';
import { t } from 'i18next';
import styles from './styles';

const WorkspaceButtonRaw = ({
  className,
  anchorEl,
  handleClose,
  isMobile,
}) => {
  const navigateRedirect = useNavigateRedirect();

  const {
    workspace: {
      workspaces,
      defaultWorkspace,
      handleNavigateByIndex,
    },
  } = useWorkspaceContext();

  const activeWorkspaces = workspaces?.filter((el) => el?.currentUser?.status === STATUS_ACTIVE);

  if (!defaultWorkspace || workspaces.length === 1) return null;

  const handleManageWorkspaces = () => {
    handleClose();
    navigateRedirect('/settings/workspaces/mine');
  };

  const handleManageWorkspacesViews = () => {
    handleClose();
    navigateRedirect('/settings/workspaces/views/mine');
  };

  const open = Boolean(anchorEl);

  const handleOnChange = (workspaceId) => {
    const index = workspaces.findIndex((el) => el.id === workspaceId);
    handleNavigateByIndex(index);
    handleClose();
  };

  const workspaceList = activeWorkspaces.filter(({
    id,
    parentId,
  }) => !parentId && id !== defaultWorkspace.id)
    .sort((a, b) => -b.name.localeCompare(a.name));

  const workspaceViewList = activeWorkspaces.filter(({
    id,
    parentId,
  }) => parentId && id !== defaultWorkspace.id)
    .sort((a, b) => -b.name.localeCompare(a.name));

  const hasWorkspaces = workspaceList.length >= 1;
  const hasViews = workspaceViewList.length >= 1;

  return (
    <>
      <Button
        className={className}
        variant="outlined"
        color="black"
        startIcon={<ArrowDownSLine />}
        size="xxsmall"
        key="workspaces-button"
      />
      {open && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: isMobile ? 'top' : 'bottom',
            horizontal: isMobile ? 'right' : 'right',
          }}
          transformOrigin={{
            vertical: isMobile ? 'bottom' : 110,
            horizontal: isMobile ? 'right' : -24,
          }}
          sx={{
            zIndex: 3000,
            maxHeight: '350px',
            overflow: 'auto',
            '.DnkPaper-root': {
              marginTop: '0 !important',
            },
          }}
        >
          {hasWorkspaces && (
            <Divider
              variant="solid"
              textAlign="left"
              sx={{
                fontSize: '12px !important',
                padding: '6px 10px',
              }}
            >
              {t('Workspaces')}
            </Divider>
          )}

          {workspaceList.map(({ id, name }) => (
            <MenuItem
              key={id}
              onClick={() => handleOnChange(id)}
            >
              <Typography sx={{ fontSize: '14px', color: 'neutral.900' }}>{name}</Typography>
            </MenuItem>
          ))}

          {hasViews && (
            <Divider
              variant="solid"
              textAlign="left"
              sx={{
                fontSize: '12px !important',
                padding: '6px 10px',
              }}
            >
              {t('Views')}
            </Divider>
          )}

          {workspaceViewList.map(({ id, name }) => (
            <MenuItem
              key={id}
              onClick={() => handleOnChange(id)}
            >
              <Typography sx={{ fontSize: '14px', color: 'neutral.900' }}>{name}</Typography>
            </MenuItem>
          ))}

          {(hasWorkspaces || hasViews) && (
            <Divider />
          )}

          <Button
            variant="text"
            size="small"
            fullWidth
            onClick={handleManageWorkspaces}
          >
            {t('Manage workspaces')}
          </Button>

          <Divider />

          <Button
            variant="text"
            size="small"
            fullWidth
            onClick={handleManageWorkspacesViews}
          >
            {t('Manage views')}
          </Button>

        </Menu>
      )}
    </>
  );
};

WorkspaceButtonRaw.propTypes = {
  className: PropTypes.string,
  anchorEl: PropTypes.object,
  handleClose: PropTypes.func,
  isMobile: PropTypes.bool,
};

const WorkspaceButton = styled(WorkspaceButtonRaw)`${styles}`;

WorkspaceButton.propTypes = WorkspaceButtonRaw.propTypes;

export default WorkspaceButton;
